import { type ReactNode, useMemo } from "react";
import type { Permission } from "~/models/permission";
import { type PermissionArgs, usePermissions } from "./helpers";

type IPermissionGuardProps = {
  children: ReactNode | ((props: { isAuthorized: boolean }) => ReactNode);
  context?: Omit<PermissionArgs, "user">;
  fallback?: ReactNode;
  permissions: Array<Permission | RegExp>;
};

export function PermissionGuard({
  children,
  context,
  fallback,
  permissions,
}: IPermissionGuardProps) {
  const allPermissions = usePermissions(context);

  const isAuthorized = useMemo(() => {
    return (
      permissions.length === 0 ||
      permissions.some((permission) =>
        typeof permission === "string"
          ? allPermissions.includes(permission)
          : allPermissions.some((up) => permission.test(up)),
      )
    );
  }, [permissions, allPermissions]);

  if (typeof children === "function") {
    return children({ isAuthorized });
  }

  return isAuthorized ? children : (fallback ?? null);
}
